import type ExcelJS from 'exceljs';
import {
    activeValueNames,
    attributeColumnTypes,
    CATEGORY_SHEET_HEADERS,
    categoryAxesHint,
    columnHeader,
    TEMPLATE_FIRST_BODY_ROW,
    TEMPLATE_LAST_BODY_ROW,
    TEMPLATE_SHEET_NAMES,
    TEMPLATE_UNITS,
    TEMPLATE_VAT_VALUES,
    templateColumns,
    uniqueSheetName,
    type TemplateAttributeType,
    type TemplateCategory,
    type TemplateCellFormat,
    type TemplateColumn,
    type TemplateLanguage,
} from './catalogImportTemplateMeta';

/**
 * exceljs keeps range validations (`worksheet.dataValidations.add('D2:D2001',
 * …)`) and writes each as one `sqref`, but leaves them out of its typings.
 * Per-cell validations are typed, and on write exceljs merges them into
 * ranges after sorting addresses as text: `D10` before `D2`, so every column
 * came out as two overlapping ranges (`D10:D2001` and `D2:D2001`), which Excel
 * treats as a fault to repair.
 */
declare module 'exceljs' {
    interface Worksheet {
        readonly dataValidations: {
            add: (range: string, validation: DataValidation) => void;
            /** Address or range → its validation, as added. */
            readonly model: Readonly<
                Record<string, DataValidation | undefined>
            >;
        };
    }
}

/**
 * Builds the catalog-import template at download time, so its dropdowns list
 * the organisation's own categories, brands, suppliers and attribute values.
 * exceljs (~1.4 MB) is loaded with a dynamic `import()`, as the purchase
 * template does, so it stays out of the main bundle until someone downloads.
 *
 * Pure of React and i18next: the caller passes the language and the data, and
 * serializes the workbook with `xlsxBlobOf` + `downloadBlob`
 * (`@/utils/fileDownload`) under `templateFilename(language)`.
 */

export type CatalogImportTemplateInput = {
    language: TemplateLanguage;
    categories: readonly TemplateCategory[];
    brands: readonly { name: string }[];
    suppliers: readonly { name: string }[];
    attributeTypes: readonly TemplateAttributeType[];
};

/** One dropdown column of the Products sheet. */
export type TemplateDropdown = {
    /** 1-based column number. */
    column: number;
    /** A workbook defined name over a reference sheet, or a quoted inline list. */
    formula: string;
    firstRow: number;
    lastRow: number;
};

/** What the builder wrote, for tests and for the caller. */
export type CatalogImportTemplateMeta = {
    language: TemplateLanguage;
    /** The Products columns in order, each with the header written for this language. */
    columns: { column: TemplateColumn; header: string }[];
    sheetNames: {
        products: string;
        categories: string;
        brands: string;
        suppliers: string;
        /** Attribute type id → its lookup sheet. */
        attributes: Record<string, string>;
    };
    dropdowns: TemplateDropdown[];
};

export type CatalogImportTemplate = {
    workbook: ExcelJS.Workbook;
    meta: CatalogImportTemplateMeta;
};

const DEFINED_NAMES = {
    categories: 'CategoryList',
    brands: 'BrandList',
    suppliers: 'SupplierList',
} as const;

const attributeDefinedName = (index: number): string =>
    `AttributeValues${index + 1}`;

const NUMBER_FORMATS: Readonly<Record<TemplateCellFormat, string>> = {
    // Text, so a long barcode or IMEI is kept as typed.
    text: '@',
    number: '0',
    amount: '0.00',
    date: 'yyyy-mm-dd',
};

/** Quote a sheet name for a range reference. */
const sheetRef = (name: string): string => `'${name.replace(/'/g, "''")}'`;

/** An inline list for a data validation: `"a,b"`. */
const inlineList = (values: readonly string[]): string =>
    `"${values.join(',')}"`;

/**
 * Fills column A from row 2 with `values` and names the range. The name
 * covers at least A2, so it resolves even over an empty list.
 */
const nameValueRange = (
    workbook: ExcelJS.Workbook,
    sheet: ExcelJS.Worksheet,
    values: readonly string[],
    definedName: string,
): void => {
    values.forEach((value, index) => {
        sheet.getCell(index + 2, 1).value = value;
    });
    const lastRow = Math.max(values.length + 1, 2);
    workbook.definedNames.add(
        `${sheetRef(sheet.name)}!$A$2:$A$${lastRow}`,
        definedName,
    );
};

const addListSheet = (
    workbook: ExcelJS.Workbook,
    name: string,
    header: string,
    values: readonly string[],
    definedName: string,
): void => {
    const sheet = workbook.addWorksheet(name);
    sheet.getColumn(1).width = 32;
    sheet.getCell(1, 1).value = header;
    sheet.getRow(1).font = { bold: true };
    sheet.views = [{ state: 'frozen', ySplit: 1 }];
    nameValueRange(workbook, sheet, values, definedName);
};

export const buildCatalogImportTemplate = async ({
    language,
    categories,
    brands,
    suppliers,
    attributeTypes,
}: CatalogImportTemplateInput): Promise<CatalogImportTemplate> => {
    const { Workbook } = (await import('exceljs')).default;
    const workbook = new Workbook();

    const attributeSheets: Record<string, string> = {};
    const sheetNames = {
        products: TEMPLATE_SHEET_NAMES.products[language],
        categories: TEMPLATE_SHEET_NAMES.categories[language],
        brands: TEMPLATE_SHEET_NAMES.brands[language],
        suppliers: TEMPLATE_SHEET_NAMES.suppliers[language],
        attributes: attributeSheets,
    };
    const taken = new Set(
        [
            sheetNames.products,
            sheetNames.categories,
            sheetNames.brands,
            sheetNames.suppliers,
        ].map((name) => name.toLowerCase()),
    );

    // Products first: it is the sheet the file opens on. The validations name
    // the reference ranges as plain strings, so those sheets can come after.
    const products = workbook.addWorksheet(sheetNames.products);
    products.views = [{ state: 'frozen', ySplit: 1 }];

    // Categories: name as stored, parent, and the axes hint.
    const categorySheet = workbook.addWorksheet(sheetNames.categories);
    categorySheet.getRow(1).values = [
        CATEGORY_SHEET_HEADERS.name[language],
        CATEGORY_SHEET_HEADERS.parent[language],
        CATEGORY_SHEET_HEADERS.hint[language],
    ];
    categorySheet.getRow(1).font = { bold: true };
    categorySheet.views = [{ state: 'frozen', ySplit: 1 }];
    categorySheet.getColumn(1).width = 32;
    categorySheet.getColumn(2).width = 28;
    categorySheet.getColumn(3).width = 60;
    const nameById = new Map(
        categories.map((category) => [category.id, category.name]),
    );
    categories.forEach((category, index) => {
        const row = index + 2;
        categorySheet.getCell(row, 2).value =
            category.parentId === null
                ? ''
                : (nameById.get(category.parentId) ?? '');
        categorySheet.getCell(row, 3).value = categoryAxesHint(
            category.id,
            attributeTypes,
            language,
        );
    });
    nameValueRange(
        workbook,
        categorySheet,
        categories.map((category) => category.name),
        DEFINED_NAMES.categories,
    );

    addListSheet(
        workbook,
        sheetNames.brands,
        CATEGORY_SHEET_HEADERS.name[language],
        brands.map((brand) => brand.name),
        DEFINED_NAMES.brands,
    );
    addListSheet(
        workbook,
        sheetNames.suppliers,
        CATEGORY_SHEET_HEADERS.name[language],
        suppliers.map((supplier) => supplier.name),
        DEFINED_NAMES.suppliers,
    );

    // One lookup sheet per attribute column, named after its type.
    const attributeNames = new Map<string, string>();
    attributeColumnTypes(attributeTypes).forEach((type, index) => {
        const name = uniqueSheetName(type.name, taken);
        taken.add(name.toLowerCase());
        sheetNames.attributes[type.id] = name;
        const definedName = attributeDefinedName(index);
        attributeNames.set(type.id, definedName);
        addListSheet(
            workbook,
            name,
            type.name,
            activeValueNames(type),
            definedName,
        );
    });

    // The Products header row, formats and dropdowns.
    const columns = templateColumns(attributeTypes).map((column) => ({
        column,
        header: columnHeader(column, language),
    }));
    products.getRow(1).values = columns.map(({ header }) => header);
    products.getRow(1).font = { bold: true };

    const listFor = (column: TemplateColumn): string | null => {
        if (column.kind === 'attribute') {
            return attributeNames.get(column.attributeTypeId) ?? null;
        }
        switch (column.key) {
            case 'category':
                return DEFINED_NAMES.categories;
            case 'brand':
                return DEFINED_NAMES.brands;
            case 'supplier':
                return DEFINED_NAMES.suppliers;
            case 'unit':
                return inlineList(TEMPLATE_UNITS);
            case 'vatType':
                return inlineList([
                    TEMPLATE_VAT_VALUES.standard[language],
                    TEMPLATE_VAT_VALUES.exempt[language],
                ]);
            default:
                return null;
        }
    };

    const dropdowns: TemplateDropdown[] = [];
    columns.forEach(({ column }, index) => {
        const columnNumber = index + 1;
        const sheetColumn = products.getColumn(columnNumber);
        sheetColumn.width = column.width;
        sheetColumn.numFmt =
            NUMBER_FORMATS[column.kind === 'field' ? column.format : 'text'];

        const formula = listFor(column);
        if (formula === null) {
            return;
        }
        dropdowns.push({
            column: columnNumber,
            formula,
            firstRow: TEMPLATE_FIRST_BODY_ROW,
            lastRow: TEMPLATE_LAST_BODY_ROW,
        });
        // One range per column. A dropdown suggests and never blocks: a new
        // brand, category, supplier or value is part of the flow, so no error
        // alert is switched on.
        const letter = sheetColumn.letter;
        products.dataValidations.add(
            `${letter}${TEMPLATE_FIRST_BODY_ROW}:${letter}${TEMPLATE_LAST_BODY_ROW}`,
            { type: 'list', allowBlank: true, formulae: [formula] },
        );
    });

    return {
        workbook,
        meta: { language, columns, sheetNames, dropdowns },
    };
};
